import React from "react";
import Container from "@mui/material/Container";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import Box from "components/common/Box";
import Stack from "components/common/Stack";
import Text from "components/common/Text";
import Table from "components/common/Table";
import Pagination from "components/common/Pagination";
import { dateTimeHelper } from "helper/dateTime";
import { formatHelper } from "helper/format";
import BillingTransactionMobileCard from "./BillingTransactionMobileCard";
import ManagePlanCard from "./ManagePlanCard";
import {
  STATUS_COLOR,
  getBillingPlanType,
  getBillingStatusLabel,
  getProductMeta,
} from "./billingHelpers";

const CELL_SX = {
  fontSize: { xs: 13, md: 15 },
  fontWeight: 500,
  color: "#111827",
  lineHeight: 1.4,
};

const IconSquareButton = ({ onClick, ariaLabel, src, rotate = 0 }) => (
  <Box
    component="button"
    type="button"
    aria-label={ariaLabel}
    onClick={onClick}
    sx={{
      width: 40,
      height: 40,
      borderRadius: "10px",
      border: "1px solid #E5E7EB",
      bgcolor: "#fff",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      p: 0,
      flexShrink: 0,
      "&:hover": { bgcolor: "#F9FAFB", borderColor: "#CBD5E1" },
    }}
  >
    <Box
      component="img"
      src={src}
      alt=""
      sx={{
        width: 20,
        height: 20,
        transform: rotate ? `rotate(${rotate}deg)` : "none",
      }}
    />
  </Box>
);

const manageColumns = [
  {
    field: "createdAt",
    headerText: "Transaction Date",
    bodyCellComponent: (v) => (
      <Text sx={CELL_SX}>
        {v ? dateTimeHelper.format(v, "$DD/$MM/$YYYY") : "NA"}
      </Text>
    ),
  },
  {
    field: "planType",
    headerText: "Plan Type",
    bodyCellComponent: (_v, row) => (
      <Text sx={CELL_SX}>{getBillingPlanType(row)}</Text>
    ),
  },
  {
    field: "paymentStatus",
    headerText: "Status",
    bodyCellComponent: (v) => (
      <Text
        sx={{
          ...CELL_SX,
          color: STATUS_COLOR[v] || "#111827",
        }}
      >
        {getBillingStatusLabel(v)}
      </Text>
    ),
  },
  {
    field: "paymentAmount",
    headerText: "Subscription Fee",
    bodyCellComponent: (v) => (
      <Text sx={CELL_SX}>
        {v ? formatHelper.currency(v) : formatHelper.currency(0)}
      </Text>
    ),
  },
];

/**
 * Figma 7942:93644 — Manage Subscription / Product Plan Detail.
 * Presentational: data loading & cancel wiring stay in the container.
 */
const ManageSubscriptionPage = ({
  productCode,
  subscriptionDetails = {},
  customerSubscriptionId,
  reasonsDropdown = [],
  planDetails = {},
  onUpgrade,
  onRenew,
  onBack,
  transactionHistory = [],
  transactionHistoryCount = 0,
  transactionHistoryLoading = false,
  page = 1,
  limit = 25,
  onPageChange,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const meta = getProductMeta(productCode);

  return (
    <Container
      maxWidth="xl"
      sx={{ px: { xs: 2, sm: 3, md: 4, lg: 8 }, py: { xs: 2.5, md: 4 } }}
    >
      <Stack spacing={{ xs: 2, md: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <IconSquareButton
            ariaLabel="Back to profile"
            onClick={onBack}
            src="/images/profile-v2/billing/arrow-right.svg"
            rotate={180}
          />
          <Box>
            <Text
              sx={{
                fontWeight: 800,
                fontSize: { xs: 20, sm: 22, md: 24 },
                color: "#111827",
                lineHeight: 1.25,
              }}
            >
              {meta.displayName}
            </Text>
            <Text
              sx={{
                mt: 0.5,
                fontSize: 14,
                color: "rgba(0,0,0,0.7)",
                lineHeight: 1.4,
              }}
            >
              {meta.group}
            </Text>
          </Box>
        </Stack>

        <ManagePlanCard
          productCode={productCode}
          subscriptionDetails={subscriptionDetails}
          customerSubscriptionId={customerSubscriptionId}
          reasonsDropdown={reasonsDropdown}
          planDetails={planDetails}
          onUpgrade={onUpgrade}
          onRenew={onRenew}
        />

        <Box
          sx={{
            bgcolor: "#fff",
            border: "1px solid #E2E8F0",
            borderRadius: { xs: "14px", md: "20px" },
            boxShadow: "0px 4px 10px rgba(0,0,0,0.02)",
            px: { xs: 2, md: 4 },
            py: { xs: 2, md: 4 },
          }}
        >
          <Text
            sx={{
              fontWeight: 700,
              fontSize: { xs: 16, md: 20 },
              color: "#111827",
              mb: { xs: 2, md: 3 },
              lineHeight: 1.3,
            }}
          >
            Transaction History
          </Text>

          {!transactionHistory.length ? (
            <Stack alignItems="center" justifyContent="center" sx={{ py: 5 }}>
              <Text sx={{ fontWeight: 500, color: "#6B7280", fontSize: 14 }}>
                {transactionHistoryLoading
                  ? "Loading transactions…"
                  : "No transaction record found"}
              </Text>
            </Stack>
          ) : !isMobile ? (
            <Box
              sx={{
                overflowX: "auto",
                "& thead th": {
                  backgroundColor: "#fff !important",
                },
                "& thead th .MuiSvgIcon-root": {
                  fontSize: 18,
                  color: "#9CA3AF",
                  opacity: 0.85,
                },
              }}
            >
              <Table
                columns={manageColumns}
                count={transactionHistoryCount}
                data={transactionHistory}
                loading={transactionHistoryLoading}
                paginationBottom
                page={page - 1}
                rowsPerPage={limit}
                onPageChange={onPageChange}
                th_sx={{
                  p: "10px 12px 12px 0",
                  backgroundColor: "#fff",
                  color: "#9CA3AF",
                  borderBottom: "1px solid #E5E7EB",
                  borderRight: "none",
                  fontWeight: 500,
                  fontSize: "13px",
                }}
                td_sx={{
                  p: "14px 12px 14px 0",
                  borderRight: "none",
                  borderBottom: "1px solid #F1F5F9",
                  fontSize: "15px",
                  fontWeight: 500,
                  verticalAlign: "middle",
                }}
              />
            </Box>
          ) : (
            <Box>
              <Pagination
                count={transactionHistoryCount}
                page={page - 1}
                rowsPerPage={limit}
                onPageChange={onPageChange}
              />
              {transactionHistory.map((data, index) => (
                <Box
                  key={
                    data?.customerPaymentId ||
                    data?.createdAt ||
                    `${productCode}-${index}`
                  }
                  sx={{
                    py: 1.75,
                    borderBottom:
                      index < transactionHistory.length - 1
                        ? "1px solid #E5E7EB"
                        : "none",
                  }}
                >
                  <BillingTransactionMobileCard
                    data={data}
                    showProductName={false}
                  />
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Stack>
    </Container>
  );
};

export default ManageSubscriptionPage;
